import type { IconNode } from 'lucide-react';

import { logger } from '@/lib/logger';

/**
 * The Lucide icon shapes this document knows, keyed by kebab-case icon name.
 *
 * `null` records a name Lucide does not have, so it is looked up only once. A
 * missing key is an icon that has not been loaded yet.
 *
 * A shape arrives in one of two ways. A page render sends the shapes of the
 * icons its source names, and `registerIconNodes` stores them before the page
 * renders, so those icons are drawn in the first commit. Every other icon is
 * fetched by `loadIconNode` after the component that needs it mounts.
 */
const nodes = new Map<string, IconNode | null>();
const pending = new Map<string, Promise<void>>();
const listeners = new Set<() => void>();

/**
 * Lucide's name-to-import map is only ever loaded through this dynamic import,
 * which keeps it off the critical path.
 */
export function loadIconImports() {
  return import('./lucideIconImports');
}

/**
 * Safe to call while rendering: it adds entries that no mounted icon has read
 * yet and notifies nobody.
 */
export function registerIconNodes(
  icons: Record<string, Array<[string, Record<string, string>]>> | null | undefined
): void {
  if (!icons) {
    return;
  }

  for (const [name, node] of Object.entries(icons)) {
    if (!nodes.has(name)) {
      nodes.set(name, node as IconNode);
    }
  }
}

export function readIconNode(name: string): IconNode | null | undefined {
  return nodes.get(name);
}

export function subscribeToIconNodes(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

async function fetchIconNode(name: string): Promise<IconNode | null> {
  const { lucideIconImports } = await loadIconImports();

  if (!Object.hasOwn(lucideIconImports, name)) {
    return null;
  }

  const icon = await lucideIconImports[name as keyof typeof lucideIconImports]();

  return icon.__iconData.node;
}

/**
 * A failed fetch stores nothing, so the icon stays blank instead of turning
 * into the unknown-name circle, and a later mount tries again.
 */
export function loadIconNode(name: string): Promise<void> {
  if (nodes.has(name)) {
    return Promise.resolve();
  }

  let load = pending.get(name);

  if (!load) {
    load = fetchIconNode(name)
      .then((node) => {
        nodes.set(name, node);
        listeners.forEach((listener) => listener());
      })
      .catch((error: unknown) => {
        logger.error('[SafeDynamicIcon] Failed to load icon', { error, context: { name } });
      })
      .finally(() => {
        pending.delete(name);
      });
    pending.set(name, load);
  }

  return load;
}

export function resetIconNodesForTest(): void {
  nodes.clear();
  pending.clear();
}
